import React, { memo, useEffect } from 'react';

import ContentLoader from 'react-content-loader';
import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';

import {
    Accordion,
    AccordionPanel,
    Button,
    Heading,
    RadioGroup,
    Section,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { compareTrimFeaturesClick } from 'reaxl-analytics-handlers';

import {
    kbbVRSDataDuck,
    preorderDuck,
    queryDuck,
} from '@/ducks';

import {
    bapActiveInteractionDuck,
    bapActiveModelDuck,
    bapModelInfoDuck,
} from '@/ducks/bap';

import CompareTrimsContainer from '@/containers/CompareTrimsContainer';

import getBapTrimId from './utilities/getBapTrimId';

const renderOptionLabel = ({ bodyStyle, cabType, doors, trimName }, doorTypes, bodyTypes) => {
    let duplicateTrimDescription = '';
    if (cabType) {
        duplicateTrimDescription = cabType;
    }
    if (doorTypes.length > 1) {
        const spacer = duplicateTrimDescription ? ' ' : '';
        duplicateTrimDescription += spacer + doors + ' door';
    }
    if (bodyTypes.length > 1) {
        const spacer = duplicateTrimDescription ? ' ' : '';
        duplicateTrimDescription += spacer + bodyStyle;
    }

    return (
        <span>
            {trimName}
            {duplicateTrimDescription && (
                <span className="margin-left-1 text-gray">
                    (
                    {duplicateTrimDescription}
                    )
                </span>
            )}
        </span>
    );
};

// each value should be unique in case trimId is duplicated for multiple trim names
const getOptionValues = (options, doorTypes, bodyTypes) => (options || []).map((option) => ({
    label: renderOptionLabel(option, doorTypes, bodyTypes),
    value: getBapTrimId(option),
}));

const setTitle = (title, isCapitalized, isSubTitle) => (
    <span
        key={`${title}-title`}
        className={clsx({
            'text-capitalize': isCapitalized,
            'text-size-400': !isSubTitle,
            'text-size-300 text-bold': isSubTitle,
            'margin-left-2': true,
        })}
    >
        {title}
    </span>
);

const TrimHighlights = memo(({ isXS }) => {
    const dispatch = useDispatch();
    const query = useSelector(queryDuck.selectors.getDuckState);
    const kbbVRSData = useSelector(kbbVRSDataDuck.selectors.getData) || {};

    const makeCodeList = query?.makeCode || query?.makeCodeList;
    const makeCode = !Array.isArray(makeCodeList) && makeCodeList;
    const modelCode = useSelector(bapActiveModelDuck.selectors.getDuckState) || '';
    const selectedTrim = useSelector(bapModelInfoDuck.selectors.getDuckState);

    const handleCompareTrimFeaturesButtonClick = (e) => {
        dispatch(bapActiveInteractionDuck.creators.setKeys({
            showTrimsModal: true,
        }));
        const modelData = kbbVRSData[makeCode]?.[modelCode];
        const trimValue = modelData && Object.keys(modelData).find((item) => item === selectedTrim);
        sendClick(compareTrimFeaturesClick, e, {
            inventoryId: 'preorderBAP',
            trimSelected: trimValue?.trimName?.toLowerCase(),
            selectedVrsData: kbbVRSData?.[makeCode]?.[modelCode]?.[selectedTrim],
        });
    };

    return (
        <Section
            data-cmp="trimFeaturesContainer"
            alignment="left"
            uiContext="section-2"
            className="padding-bottom-3"
        >
            <Button
                data-cmp="compareTrimsButton"
                className="btn-secondary text-size-200 btn-sm"
                onClick={handleCompareTrimFeaturesButtonClick}
            >
                Compare Trim Features
            </Button>
        </Section>
    );
});

const renderTrimSubsections = (trims, selectedTrim, handleTrimChange) => {
    const bodyTypes = [];
    const engineTypes = [];
    const doorTypes = [];

    trims.forEach((trim) => {
        if (!bodyTypes.includes(trim.bodyStyle)) {
            bodyTypes.push(trim.bodyStyle);
        }
        if (!engineTypes.includes(trim.engineType)) {
            engineTypes.push(trim.engineType);
        }
        if (!doorTypes.includes(trim.doors)) {
            doorTypes.push(trim.doors);
        }
    });

    return engineTypes.map((type) => {
        const optionValues = trims.filter((trim) => trim.engineType === type);

        return (
            <div
                className="padding-bottom-4"
                key={`${type}-subsection`}
            >
                <Heading
                    size={300}
                    color="subdued-lightest"
                    className="padding-bottom-1"
                >
                    {type === 'Gas' ? 'Gasoline' : type}
                </Heading>
                <div>
                    <RadioGroup
                        name="radioGroup"
                        layout="vertical"
                        options={getOptionValues(optionValues, doorTypes, bodyTypes)}
                        onChange={handleTrimChange}
                        value={selectedTrim || 'No Trim Selected'}
                        key="trim-radio-group"
                    />
                </div>
            </div>
        );
    });
};

const renderTrimsPlaceholder = (
    <ContentLoader
        width="100%"
        height="110px"
        speed={2}
        backgroundColor="#e1e4e8"
        foregroundColor="#f8f8f8"
    >
        <rect
            x="0"
            y="0"
            width="100%"
            height="20"
        />
        <rect
            x="0"
            y="30"
            width="100%"
            height="20"
        />
        <rect
            x="0"
            y="60"
            width="100%"
            height="20"
        />
    </ContentLoader>
);

function BuildAndPriceTrimsSection({
    device,
    handleTrimChange,
}) {
    const dispatch = useDispatch();
    const isXS = device?.is?.xs || false;
    const isLoading = useSelector(kbbVRSDataDuck.selectors.getTrimsLoading);
    const showTrimsModal = useSelector(bapActiveInteractionDuck.selectors.getShowTrimsModal);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const makeCodeList = query?.makeCode || query?.makeCodeList;
    const makeCode = !Array.isArray(makeCodeList) && makeCodeList;
    const modelCode = useSelector(bapActiveModelDuck.selectors.getDuckState) || '';
    const kbbVRSData = useSelector(kbbVRSDataDuck.selectors.getData) || {};
    const trimsData = Object.values(_get(kbbVRSData, [makeCode, modelCode], []));
    const activeTrims = useSelector(kbbVRSDataDuck.selectors.getSelectedMakeModelTrims);
    const selectedTrim = useSelector(bapModelInfoDuck.selectors.getDuckState);
    const loadTrimData = (options) => dispatch(preorderDuck.creators.loadTrimData(options));
    const setActiveInteraction = (options) => dispatch(bapActiveInteractionDuck.creators.setKeys(options));
    const loadVehicleSpecs = () => dispatch(kbbVRSDataDuck.creators.loadVehicleSpecs());

    useEffect(() => {
        if (showTrimsModal) {
            loadVehicleSpecs();
            loadTrimData({ activeTrims });

            const activeTrimsFuelTypes = Array.from(new Set(activeTrims.map((trim) => trim.engineType)));

            if (activeTrimsFuelTypes.length > 0) {
                setActiveInteraction({
                    fuelTypeOptions: activeTrimsFuelTypes.map((value) => {
                        const label = value === 'Gas' ? 'Gasoline' : value;
                        return { label, value };
                    }),
                });
            }
        }
    });

    return (
        <>
            <Accordion
                bsStyle="filters"
                className="text-left"
                style={{ borderBottom: '1px solid #e1e4e8', borderTop: '1px solid #e1e4e8' }}
            >
                <AccordionPanel
                    title={setTitle('Trim')}
                    key="trim-options"
                    eventKey="trimOptions"
                >
                    {(!isLoading && trimsData.length > 0)
                        ? renderTrimSubsections(trimsData, selectedTrim, handleTrimChange)
                        : renderTrimsPlaceholder}
                    <TrimHighlights isXS={isXS} />
                </AccordionPanel>
            </Accordion>
            {showTrimsModal && <CompareTrimsContainer modal />}
        </>
    );
}

export default memo(BuildAndPriceTrimsSection);
